import React, { useEffect, useState } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { DatePickerInput } from "react-native-paper-dates";

import {
  useGetAssessmentAiTutorsQuery,
  useGetAssessmentQuestionBankLazyQuery,
  Module,
  AiTutor,
  ActivityState,
  AssessmentType,
} from "../../../../graphql/generated/graphql";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import FormLayout from "../common/FormLayout";
import AssessmentFormAccordion from "./AssessmentFormAccordion";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import AssessmentQuestionBankModal, { type QuestionBankAiTutor } from "./AssessmentQuestionBankModal";
import RadioButton from "../../../common/form/RadioButton/RadioButton";

interface ActivityData {
  id?: string;
  assessmentType?: AssessmentType;
  numberOfQuestions?: number;
  timeLimit?: number;
  selectedAiTutorIds?: string[];
  dueDate?: string | Date | null;
  activityState: ActivityState | undefined;
  scheduledDate: string | Date | undefined;
}

interface AssessmentFormProps {
  activityData: ActivityData;
  onSave: (data: ActivityData) => void;
  onCancel: () => void;
  error: string;
  isSubmitting: boolean;
  isNewActivity: boolean;
  activeModuleData: Module;
  courseId: string;
}

const DEFAULT_NUMBER_OF_QUESTIONS = 10;
const DEFAULT_TIME_LIMIT = 25;
const MAX_NUMBER_OF_QUESTIONS = 25;
const MAX_TIME_LIMIT = 30;
const DEFAULT_ASSESSMENT_TYPE: AssessmentType = AssessmentType.SocraticDialog;
const MCQ_NOT_IMPLEMENTED_ERROR = "Multiple choice (MCQ) assessments are not implemented yet.";
const NO_AI_TUTORS_ERROR = "Please select at least one interactive learning experience";

const maybeFormatDueDate = (date: unknown): Date | null => {
  if (!date) return null;
  if (date instanceof Date) return date;
  if (typeof date === "string") {
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const AssessmentForm = ({
  activityData,
  onSave,
  onCancel,
  error,
  isSubmitting,
  isNewActivity = false,
  activeModuleData,
  courseId,
}: AssessmentFormProps) => {
  const { data: assessmentAiTutors } = useGetAssessmentAiTutorsQuery({
    variables: {
      moduleId: activeModuleData.id,
      courseId,
    },
    fetchPolicy: "no-cache",
    skip: !activeModuleData || !courseId,
  });

  const [validationError, setValidationError] = useState("");
  const [showPastModules, setShowPastModules] = useState(false);
  const [showFutureModules, setShowFutureModules] = useState(false);
  const [openPastAccordionIndices, setOpenPastAccordionIndices] = useState<number[]>([]);
  const [openFutureAccordionIndices, setOpenFutureAccordionIndices] = useState<number[]>([]);
  const [isQuestionBankOpen, setIsQuestionBankOpen] = useState(false);

  const assessmentId = activityData?.id;
  const canViewQuestionBank = !isNewActivity && !!assessmentId;

  const [fetchQuestionBank, { data: questionBankData, loading: questionBankLoading, error: questionBankError }] =
    useGetAssessmentQuestionBankLazyQuery({
      fetchPolicy: "network-only",
    });

  const questionBankAiTutors = (questionBankData?.getAssessmentQuestionBank?.data?.aiTutors || []).filter(
    Boolean
  ) as unknown as QuestionBankAiTutor[];

  const [selectedAssessmentType, setSelectedAssessmentType] = useState<AssessmentType>(
    activityData?.assessmentType ?? DEFAULT_ASSESSMENT_TYPE
  );

  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      assessmentType: activityData?.assessmentType ?? DEFAULT_ASSESSMENT_TYPE,
      numberOfQuestions: activityData?.numberOfQuestions,
      timeLimit: activityData?.timeLimit,
      selectedAiTutors: activityData?.selectedAiTutorIds || [],
      dueDate: maybeFormatDueDate(activityData?.dueDate),
      activityState: activityData?.activityState || "draft",
      scheduledDate: activityData?.scheduledDate || undefined,
    },
  });

  const hasSelectedAiTutors = (moduleAiTutors: { id: string; title: string }[], selectedTutorIds: string[]) => {
    return moduleAiTutors?.some((tutor) => selectedTutorIds?.includes(tutor.id));
  };

  useEffect(() => {
    if (
      (!activityData?.selectedAiTutorIds || activityData.selectedAiTutorIds.length === 0) &&
      assessmentAiTutors?.getAssessmentAiTutors?.data?.currentModuleAiTutors?.aiTutors
    ) {
      const aiTutors = assessmentAiTutors.getAssessmentAiTutors.data.currentModuleAiTutors.aiTutors;
      setValue(
        "selectedAiTutors",
        aiTutors.map((tutor) => tutor.id)
      );
    }
  }, [assessmentAiTutors, setValue, activityData?.selectedAiTutorIds]);

  const selectedAiTutors = watch("selectedAiTutors");
  const watchActivityState = watch("activityState");
  const watchAssessmentType = watch("assessmentType") ?? DEFAULT_ASSESSMENT_TYPE;

  useEffect(() => {
    if (selectedAiTutors && selectedAiTutors.length > 0 && validationError) {
      setValidationError("");
    }
  }, [selectedAiTutors, validationError]);

  const onSubmit = (data) => {
    const assessmentType: AssessmentType = data.assessmentType ?? DEFAULT_ASSESSMENT_TYPE;

    if (assessmentType === AssessmentType.Mcq) {
      setValidationError(MCQ_NOT_IMPLEMENTED_ERROR);
      return;
    }

    if (!data.selectedAiTutors || data.selectedAiTutors.length === 0) {
      setValidationError(NO_AI_TUTORS_ERROR);
      return;
    }

    setValidationError("");
    const payload = {
      assessmentId: activeModuleData.assessment?.id,
      assessmentType,
      numberOfQuestions: data.numberOfQuestions || DEFAULT_NUMBER_OF_QUESTIONS,
      timeLimit: data.timeLimit || DEFAULT_TIME_LIMIT,
      aiTutorIds: data.selectedAiTutors,
      type: "Assessment",
      dueDate: data.dueDate ? data.dueDate : null,
      activityState: data.activityState,
      scheduledDate: data.scheduledDate,
    };

    onSave(payload);
  };

  const handleOpenQuestionBank = async () => {
    if (!canViewQuestionBank || !assessmentId) return;
    setIsQuestionBankOpen(true);
    await fetchQuestionBank({ variables: { assessmentId } });
  };

  useEffect(() => {
    if (!isNewActivity && activityData?.selectedAiTutorIds?.length > 0) {
      const selectedIds = activityData.selectedAiTutorIds;

      if (assessmentAiTutors?.getAssessmentAiTutors?.data?.pastModulesAiTutors?.length > 0) {
        const pastModulesWithSelections = assessmentAiTutors?.getAssessmentAiTutors?.data?.pastModulesAiTutors
          .map((module, index) => ({ module, index }))
          .filter(({ module }) => hasSelectedAiTutors(module.aiTutors, selectedIds))
          .map(({ index }) => index);

        if (pastModulesWithSelections.length > 0) {
          setShowPastModules(true);
          setOpenPastAccordionIndices(pastModulesWithSelections);
        }
      }

      if (assessmentAiTutors?.getAssessmentAiTutors?.data?.futureModulesAiTutors?.length > 0) {
        const futureModulesWithSelections = assessmentAiTutors?.getAssessmentAiTutors?.data?.futureModulesAiTutors
          .map((module, index) => ({ module, index }))
          .filter(({ module }) => hasSelectedAiTutors(module.aiTutors, selectedIds))
          .map(({ index }) => index);

        if (futureModulesWithSelections.length > 0) {
          setShowFutureModules(true);
          setOpenFutureAccordionIndices(futureModulesWithSelections);
        }
      }
    }
  }, [isNewActivity, activityData?.selectedAiTutorIds, assessmentAiTutors?.getAssessmentAiTutors?.data]);

  return (
    <FormLayout
      title={isNewActivity ? "Assessment" : "Edit Assessment"}
      isSubmitting={isSubmitting}
      buttonsContainerStyle={{ justifyContent: "space-between" }}
      submitButtonStyle={styles.submitButton}
      cancelButtonStyle={styles.cancelButton}
      onCancel={onCancel}
      onSubmit={handleSubmit(onSubmit)}
      actionText={isNewActivity ? "Generate Questions" : "Save"}
      disableSubmit={selectedAssessmentType === AssessmentType.Mcq}
      hasButtonLoader={true}
      error={error}
      contentContainerStyle={{ paddingBottom: 0 }}>
      <View style={styles.questionBankTopRight}>
        <CustomButton
          text="View Question Bank"
          useTranslationText={false}
          size="s"
          styleType="primaryLight"
          onPress={handleOpenQuestionBank}
          disabled={!canViewQuestionBank}
        />
      </View>
      {!canViewQuestionBank && (
        <>
          <CustomSpacing type="vertical" size="xs" />
          <View style={styles.questionBankTopRight}>
            <CustomText
              text={
                isNewActivity ? "Save this assessment first to view its question bank." : "Question bank not available."
              }
              useTranslationText={false}
              style={styles.subText}
              weight="normal"
            />
          </View>
        </>
      )}
      <CustomText text="Assessment Type" size="m" weight="ultraBold" useTranslationText={false} />
      <CustomText
        style={styles.subText}
        text="Select how students will complete this assessment."
        useTranslationText={false}
      />
      <CustomSpacing type="vertical" size="m" />

      <Controller
        name="assessmentType"
        control={control}
        render={({ field: { onChange, value } }) => (
          <>
            <View style={styles.assessmentTypeRow}>
              <RadioButton
                checked={(value ?? DEFAULT_ASSESSMENT_TYPE) === AssessmentType.SocraticDialog}
                onPress={() => {
                  onChange(AssessmentType.SocraticDialog);
                  setValidationError("");
                }}
                label="Socratic Dialog"
              />
            </View>
            <CustomSpacing type="vertical" size="s" />
            <View style={styles.assessmentTypeRow}>
              <RadioButton
                checked={(value ?? DEFAULT_ASSESSMENT_TYPE) === AssessmentType.Mcq}
                onPress={() => {
                  onChange(AssessmentType.Mcq);
                  setValidationError("");
                }}
                label="Multiple Choice (MCQ)"
              />
            </View>
          </>
        )}
      />

      {watchAssessmentType === AssessmentType.Mcq ? (
        <>
          <CustomSpacing type="vertical" size="m" />
          <CustomText text={MCQ_NOT_IMPLEMENTED_ERROR} size="s" useTranslationText={false} style={styles.warningText} />
        </>
      ) : null}

      <CustomSpacing type="vertical" size="xl" />

      <CustomText text="Number of Questions" size="m" weight="ultraBold" />
      <CustomText
        style={styles.subText}
        text="number_of_questions_subtitle"
        interpolation={{ maxQuestions: MAX_NUMBER_OF_QUESTIONS }}
      />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="numberOfQuestions"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="e.g. 10"
            onBlur={onBlur}
            onChangeText={(text) => {
              if (text === "") {
                onChange(undefined);
              } else {
                const num = parseInt(text, 10);
                if (!isNaN(num) && num >= 1 && num <= MAX_NUMBER_OF_QUESTIONS) {
                  onChange(num);
                }
              }
            }}
            value={value?.toString()}
            style={styles.textInput}
            keyboardType="numeric"
            maxLength={3}
          />
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Time Limit (Optional)" size="m" weight="ultraBold" />
      <CustomText style={styles.subText} text="time_limit_subtitle" interpolation={{ maxTimeLimit: MAX_TIME_LIMIT }} />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="timeLimit"
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <CustomTextInput
            placeholder="e.g. 30"
            onBlur={onBlur}
            onChangeText={(text) => {
              if (text === "") {
                onChange(undefined);
              } else {
                const num = parseInt(text, 10);
                if (!isNaN(num) && num >= 1 && num <= MAX_TIME_LIMIT) {
                  onChange(num);
                }
              }
            }}
            value={value?.toString()}
            style={styles.textInput}
            keyboardType="numeric"
            maxLength={2}
          />
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Due Date (Optional)" size="m" weight="ultraBold" />
      <CustomSpacing type="vertical" size="s" />
      <Controller
        name="dueDate"
        control={control}
        rules={{ required: false }}
        render={({ field: { onChange, value } }) => (
          <DatePickerInput
            locale="en"
            value={value instanceof Date ? value : value ? new Date(value) : undefined}
            onChange={(date) => onChange(date ?? null)}
            onChangeText={(text) => {
              if (!text || text.trim() === "") {
                onChange(null);
              }
            }}
            inputMode="start"
            mode="outlined"
            outlineColor={"#D1D5DB"}
            selectionColor={undefined}
            activeOutlineColor={"blue"}
            withDateFormatInLabel={false}
            placeholder="MM/DD/YYYY"
            theme={{
              colors: {
                primary: "#000000",
                onSurface: "#000000",
              },
              roundness: 8,
            }}
            style={styles.datePicker}
            aria-label="Due Date date picker"
          />
        )}
      />

      <CustomSpacing type="vertical" size="xl" />
      <CustomText text="Use activities from this module" weight="ultraBold" />
      <CustomText style={styles.subText} text="Select content to generate questions from." />
      <CustomSpacing type="vertical" />
      <CustomText text="Current Module" weight="ultraBold" />
      <CustomSpacing type="vertical" size="xs" />

      <AssessmentFormAccordion
        title={assessmentAiTutors?.getAssessmentAiTutors?.data?.currentModuleAiTutors.title}
        aiTutors={assessmentAiTutors?.getAssessmentAiTutors?.data?.currentModuleAiTutors.aiTutors as AiTutor[]}
        control={control}
      />

      {assessmentAiTutors?.getAssessmentAiTutors?.data?.pastModulesAiTutors?.length > 0 && (
        <>
          <TouchableOpacity
            style={styles.includeModulesButton}
            onPress={() => {
              if (showPastModules) {
                setShowPastModules(false);
                setOpenPastAccordionIndices([]);
              } else {
                setShowPastModules(true);
                setOpenPastAccordionIndices(
                  assessmentAiTutors?.getAssessmentAiTutors?.data?.pastModulesAiTutors &&
                    assessmentAiTutors?.getAssessmentAiTutors?.data?.pastModulesAiTutors.length > 0
                    ? [0]
                    : []
                );
              }
            }}>
            <CustomText
              text={showPastModules ? "hide_other_modules" : "show_other_modules"}
              style={styles.includeModulesButtonText}
            />
          </TouchableOpacity>
          {showPastModules && (
            <>
              <CustomSpacing type="vertical" size="xxl" />
              <CustomText text="Past Modules" size="m" weight="ultraBold" />
              <CustomSpacing type="vertical" size="xs" />
              {assessmentAiTutors?.getAssessmentAiTutors?.data?.pastModulesAiTutors.map((module, index) => (
                <AssessmentFormAccordion
                  key={module.id}
                  title={module.title}
                  aiTutors={module.aiTutors as AiTutor[]}
                  control={control}
                  isOpen={openPastAccordionIndices.includes(index)}
                  onToggle={() => {
                    setOpenPastAccordionIndices((prev) =>
                      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
                    );
                  }}
                />
              ))}
            </>
          )}
        </>
      )}

      {assessmentAiTutors?.getAssessmentAiTutors?.data?.futureModulesAiTutors?.length > 0 && (
        <>
          <TouchableOpacity
            style={styles.includeModulesButton}
            onPress={() => {
              if (showFutureModules) {
                setShowFutureModules(false);
                setOpenFutureAccordionIndices([]);
              } else {
                setShowFutureModules(true);
                setOpenFutureAccordionIndices(
                  assessmentAiTutors?.getAssessmentAiTutors?.data?.futureModulesAiTutors &&
                    assessmentAiTutors?.getAssessmentAiTutors?.data?.futureModulesAiTutors.length > 0
                    ? [0]
                    : []
                );
              }
            }}>
            <CustomText
              text={showFutureModules ? "hide_future_modules" : "show_future_modules"}
              style={styles.includeModulesButtonText}
            />
          </TouchableOpacity>
          {showFutureModules && (
            <>
              <CustomSpacing type="vertical" size="xxl" />
              <CustomText text="Future Modules" size="m" weight="ultraBold" />
              <CustomSpacing type="vertical" size="xs" />
              {assessmentAiTutors?.getAssessmentAiTutors?.data?.futureModulesAiTutors.map((module, index) => (
                <AssessmentFormAccordion
                  key={module.id}
                  title={module.title}
                  aiTutors={module.aiTutors as AiTutor[]}
                  control={control}
                  isOpen={openFutureAccordionIndices.includes(index)}
                  onToggle={() => {
                    setOpenFutureAccordionIndices((prev) =>
                      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
                    );
                  }}
                />
              ))}
            </>
          )}
        </>
      )}

      <CustomSpacing type="vertical" size="m" />
      <View style={styles.activityState}>
        <CustomText text="Publish this Activity" size="l" weight="ultraBold" useTranslationText={false} />
        <CustomText
          text="Publish this activity now or schedule a publish date."
          size="s"
          useTranslationText={false}
          style={styles.stepDescription}
        />
        <Controller
          control={control}
          name="activityState"
          render={({ field: { onChange, value } }) => (
            <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
              {Object.keys(ActivityState).map((state) => (
                <CustomButton
                  key={state}
                  text={ActivityState[state]}
                  size="s"
                  style={[styles.activityStateOption, ActivityState[state] === value && styles.activeState]}
                  onPress={() => {
                    onChange(ActivityState[state]);
                  }}
                />
              ))}
            </div>
          )}
        />
        {watchActivityState === ActivityState.Scheduled && (
          <>
            <CustomText
              text="This Activity will automatically be published after:"
              size="s"
              useTranslationText={false}
              style={styles.stepDescription}
            />
            <Controller
              control={control}
              name="scheduledDate"
              render={({ field: { onChange, value } }) => (
                <div style={{ maxWidth: 250, marginTop: 10 }}>
                  <DatePickerInput
                    locale="en"
                    value={value instanceof Date ? value : value ? new Date(value) : undefined}
                    onChange={onChange}
                    inputMode="start"
                    mode="outlined"
                    outlineColor={"#D1D5DB"}
                    selectionColor={undefined}
                    activeOutlineColor={"blue"}
                    withDateFormatInLabel={false}
                    placeholder="MM/DD/YYYY"
                    theme={{
                      colors: {
                        primary: "#000000",
                        onSurface: "#667085",
                      },
                      roundness: 8,
                    }}
                    style={styles.datePicker}
                    aria-label="Schedule published date picker"
                  />
                </div>
              )}
            />
          </>
        )}
      </View>

      {validationError && (
        <>
          <CustomSpacing type="vertical" size="s" />
          <CustomText text={validationError} size="s" style={styles.errorText} useTranslationText={false} />
        </>
      )}

      <AssessmentQuestionBankModal
        visible={isQuestionBankOpen}
        onClose={() => setIsQuestionBankOpen(false)}
        isLoading={questionBankLoading}
        errorMessage={questionBankError?.message ?? null}
        aiTutors={questionBankAiTutors}
      />
    </FormLayout>
  );
};

const styles = StyleSheet.create({
  textInput: {
    width: "100%",
  },
  subText: {
    color: "#667085",
    fontSize: 14,
    fontWeight: "400",
  },
  submitButton: {
    padding: 0,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "rgba(5, 6, 15, 0.10)",
    padding: 0,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
  },
  errorText: {
    color: "#E53E3E",
    fontSize: 14,
    fontWeight: "400",
  },
  includeModulesButton: {
    alignItems: "flex-start",
    marginTop: 12,
  },
  includeModulesButtonText: {
    color: "#3E68FE",
    fontSize: 14,
    fontWeight: "500",
    backgroundColor: "transparent",
    textDecorationLine: "underline",
  },
  questionBankTopRight: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  datePicker: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D5D7DA",
    borderRadius: 10,
    color: "#000000",
    fontSize: 14,
  },
  activityState: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
  },
  activeState: {
    backgroundColor: "#0084FF1A",
    color: "white",
    borderColor: "#0084FF",
  },
  activityStateOption: {
    borderRadius: 30,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 2,
    borderStyle: "solid",
    color: "#667085",
    borderColor: "transparent",
  },
  stepDescription: {
    color: "#667085",
    marginTop: 5,
  },
  assessmentTypeRow: {
    paddingVertical: 6,
  },
  warningText: {
    color: "#B54708",
    fontSize: 13,
  },
  typeHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  typeHeaderText: {
    color: "#667085",
  },
  changeTypeText: {
    color: "#3E68FE",
    fontWeight: "700",
  },
});

export default AssessmentForm;
